import { Component } from '@theme/component';
import {
  observerElement,
  resetSrcsetOnMobile,
  onIdleAfterPageLoad,
  debounce,
  isMobileBreakpoint,
} from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { DialogCloseEvent, DialogOpenEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { resolvePlaylist, pickVariant, variantToMp4 } from '@theme/hls-rendition';

const CARD_SELECTOR = '.video-reviews-card[data-video-src]';
const PREVIEW_VIDEO = 'video.video-reviews-card__preview';
const SEQUENCE_MODE = 'autoplay_sequence';
const PREVIEW_SECONDS = 2;
const PREVIEW_LOOP_STEP = 0.2;
const PREVIEW_LOOP_VARIANTS = 5;
/** Media-fragment window on preview sources; covers every staggered loop length. */
const PREVIEW_FRAGMENT_SECONDS = 3;
const PREVIEW_MAX_TIER = 720;
const POPUP_MOBILE_MAX_TIER = 720;
const POPUP_MEDIA = '.video-review-popup__media';
const POPUP_VIDEO = 'video.video-review-popup__video';
/** Slides either side of the active one that are primed for a smooth swipe. */
const POPUP_PRIME_RADIUS = 1;

/**
 * Best-effort play. A sounded attempt can be refused without a fresh gesture, so
 * it retries muted rather than leaving the card stalled. Only the autoplay policy
 * earns that retry: pausing a clip while its play() is still pending rejects with
 * AbortError, and retrying there would restart the slide the shopper just left.
 * @param {HTMLVideoElement | null | undefined} video
 */
function tryPlay(video) {
  video?.play?.().catch((error) => {
    if (error?.name !== 'NotAllowedError' || video.muted) return;

    video.muted = true;
    video.play().catch(() => {});
  });
}

/**
 * A lazily-sourced <video>: owns the element, resolves its rendition from the HLS
 * master in the background, and replays whatever playback was asked for while that
 * was still in flight. Subclasses pick the rendition and add their own playback rules.
 */
class VideoClip {
  /** @type {HTMLVideoElement | null} */
  video = null;
  /** @type {'play' | 'load' | 'unlock' | null} Requested while the source was still resolving. */
  intent = null;
  /** @type {AbortController | null} Scopes the current source's listeners. */
  #sourceController = null;
  /** Guards the async resolve, so overlapping attach() calls resolve the rendition once. */
  #attaching = false;
  /**
   * @type {AbortController | null} Scopes playback listeners to this clip. The
   * <video> outlives it — clips are rebuilt over the same elements — so leaving
   * them attached would stack a handler per rebuild.
   */
  #playbackController = null;

  /** @param {Element} media The element carrying the `data-video-*` sources. */
  constructor(media) {
    this.media = media;
  }

  /** @returns {string} Selector for this clip's <video> inside `media`. */
  get videoSelector() {
    return 'video';
  }

  /** @returns {AbortSignal | undefined} */
  get signal() {
    return this.#playbackController?.signal;
  }

  /**
   * Adopts the <video> on first use and binds this clip's playback listeners.
   * @returns {HTMLVideoElement | null}
   */
  ensureVideo() {
    if (this.video) return this.video;

    const video = this.media.querySelector(this.videoSelector);
    if (!(video instanceof HTMLVideoElement)) return null;

    this.video = video;
    this.#playbackController = new AbortController();
    this.bindPlayback(video);
    return video;
  }

  /** Wires a source if the clip has none. */
  attach() {
    const video = this.ensureVideo();
    if (video && !video.src) this.#attachSource();
    return video;
  }

  /**
   * Resolves the rendition in the background, then wires it with a one-shot fallback
   * to Liquid's mp4 in case the derived URL 404s.
   */
  async #attachSource() {
    if (this.#attaching) return;
    this.#attaching = true;

    const fallbackMp4 = this.media.getAttribute('data-video-src');
    const resolved = await this.resolveSource().finally(() => { this.#attaching = false; });
    const src = resolved || fallbackMp4;

    const { video } = this;
    if (!src || !video || video.src) return;

    this.#sourceController?.abort();
    this.#sourceController = new AbortController();

    if (fallbackMp4 && fallbackMp4 !== src) {
      video.addEventListener(
        'error',
        () => {
          video.src = this.decorateSource(fallbackMp4);
          this.applyIntent();
        },
        { once: true, signal: this.#sourceController.signal }
      );
    }

    video.src = this.decorateSource(src);
    this.sourceAttached(video);
    this.applyIntent();
  }

  /**
   * The rendition to prefer over Liquid's mp4, or null to keep the fallback.
   * @returns {Promise<string | null>}
   */
  async resolveSource() {
    return null;
  }

  /**
   * Last chance to rewrite the URL before it is assigned — media fragments.
   * @param {string} src
   * @returns {string}
   */
  decorateSource(src) {
    return src;
  }

  /**
   * Runs once per source, as soon as it is live.
   * @param {HTMLVideoElement} _video
   */
  sourceAttached(_video) {}

  /**
   * Playback listeners, scoped to `signal` so a rebuilt clip cannot stack them.
   * @param {HTMLVideoElement} _video
   */
  bindPlayback(_video) {}

  /** Replays the pending request once a source is live. Sticky; pause() and detach() clear it. */
  applyIntent() {
    const { video } = this;
    if (!video?.src || !this.intent) return;

    if (this.intent === 'play') tryPlay(video);
    else if (this.intent === 'load') this.buffer();
  }

  /** Requests playback, starting the source if it has not been wired yet. */
  play() {
    this.intent = 'play';
    this.attach();
    this.applyIntent();
  }

  pause() {
    this.intent = null;
    this.video?.pause();
  }

  /** Buffers without playing, so the clip is ready when it is handed the stage. */
  buffer() {
    const { video } = this;
    if (!video) return;

    if (this.intent !== 'play') this.intent = 'load';
    if (video.src && video.preload !== 'auto') {
      video.preload = 'auto';
      video.load();
    }
  }

  /** @param {boolean} muted */
  mute(muted) {
    if (this.video) this.video.muted = muted;
  }

  get isPlaying() {
    return Boolean(this.video && !this.video.paused);
  }

  /** Hands the decoder back, keeping the element so it can be re-attached. */
  detach() {
    const { video } = this;
    this.intent = null;
    this.#sourceController?.abort();
    this.#sourceController = null;
    if (!video?.src) return;

    video.pause();
    video.removeAttribute('src');
    video.load();
  }

  /** Releases the source and drops the element; the clip cannot be reused. */
  destroy() {
    this.detach();
    this.#playbackController?.abort();
    this.#playbackController = null;
    this.video = null;
  }
}

/**
 * One popup slide's video. The element ships with the markup and is never rebuilt;
 * leaving a slide only pauses it, and the source is released when the popup closes.
 */
class PopupClip extends VideoClip {
  /** Whether the muted play/pause that unlocks iOS sounded playback has run for this source. */
  #unlocked = false;

  get videoSelector() {
    return POPUP_VIDEO;
  }

  /**
   * Caps the rendition on small screens so a full-viewport slide never pulls 1080p.
   * @returns {Promise<string | null>}
   */
  async resolveSource() {
    const masterSrc = this.media.getAttribute('data-video-hls');
    if (!masterSrc) return null;

    const maxTier = isMobileBreakpoint() ? POPUP_MOBILE_MAX_TIER : Infinity;
    const variants = await resolvePlaylist(masterSrc);
    return variants && variantToMp4(pickVariant(variants, Infinity, { maxTier, requireTier: true }));
  }

  /**
   * Nudges WebKit to repaint its frozen first frame. Once per source: the seek
   * re-fires `playing`.
   * @param {HTMLVideoElement} video
   */
  sourceAttached(video) {
    video.addEventListener('playing', () => { video.currentTime += 0.001; }, { once: true, signal: this.signal });
  }

  /**
   * Loops by hand on touch, where native loop re-freezes the first frame.
   * @param {HTMLVideoElement} video
   */
  bindPlayback(video) {
    video.addEventListener('playing', () => this.media.classList.remove('is-paused'), { signal: this.signal });

    if (!matchMedia('(pointer: coarse)').matches) {
      video.loop = true;
      return;
    }

    video.loop = false;
    video.addEventListener(
      'ended',
      () => {
        video.currentTime = 0;
        tryPlay(video);
      },
      { signal: this.signal }
    );
  }

  applyIntent() {
    if (this.intent !== 'unlock') {
      super.applyIntent();
      return;
    }

    const { video } = this;
    if (!video?.src || !video.paused) return;

    this.#unlocked = true;
    video.muted = true;
    video.play().then(() => video.pause()).catch(() => {});
  }

  /**
   * Readies a neighbouring slide: the source is wired at `preload=metadata` rather
   * than pulling the whole clip, and the iOS unlock runs at most once per source.
   */
  prime() {
    const video = this.ensureVideo();
    if (!video) return;

    if (video.preload === 'none') video.preload = 'metadata';
    this.attach();
    this.unlock();
  }

  /** Plays muted and immediately pauses, so the gesture unlocks sounded playback. Once per source. */
  unlock() {
    if (this.#unlocked) return;
    if (this.intent !== 'play') this.intent = 'unlock';
    this.attach();
    this.applyIntent();
  }

  detach() {
    super.detach();
    this.#unlocked = false;
    this.media.classList.remove('is-paused');
  }
}

/**
 * One card's preview clip: loops the opening seconds in `autoplay_preview`, or plays
 * through to its end in `autoplay_sequence`.
 */
class CardPreview extends VideoClip {
  previewing = false;
  boundLoop = false;
  boundSequence = false;
  /** CSS width the source is matched against; read before the rendition resolves. */
  #cardWidth = 0;

  /**
   * @param {Element} media - The card element (also the `data-video-*` source).
   * @param {PreviewController} engine - Owning engine (for the current mode).
   * @param {number} [loopSeconds] - Where the preview loops back to the start.
   */
  constructor(media, engine, loopSeconds = PREVIEW_SECONDS) {
    super(media);
    this.engine = engine;
    this.loopSeconds = loopSeconds;
  }

  get videoSelector() {
    return PREVIEW_VIDEO;
  }

  /**
   * Adopting the card's <video> also starts wiring its source — a card is only
   * adopted once it is in view, so there is nothing to wait for.
   * @param {number} [cardWidth]
   * @returns {HTMLVideoElement | null}
   */
  ensureVideo(cardWidth = 0) {
    if (this.video) return this.video;

    this.#cardWidth = cardWidth;
    const video = super.ensureVideo();
    if (!video) return null;

    video.muted = true;
    this.attach();
    return video;
  }

  /** @param {HTMLVideoElement} video */
  bindPlayback(video) {
    video.addEventListener('playing', () => this.media.classList.add('is-preview-playing'), { signal: this.signal });
  }

  /**
   * A size-matched mp4, matched on CSS width — a portrait clip's renditions are only
   * 270, 404 and 606 wide, so scaling by devicePixelRatio would collapse the ladder.
   * @returns {Promise<string | null>}
   */
  async resolveSource() {
    const masterSrc = this.media.getAttribute('data-video-hls');
    if (!masterSrc) return null;

    const width = this.#cardWidth || this.media.getBoundingClientRect().width || 360;
    const maxTier = this.engine.mode === SEQUENCE_MODE ? Infinity : PREVIEW_MAX_TIER;

    const variants = await resolvePlaylist(masterSrc);
    return variants && variantToMp4(pickVariant(variants, Math.ceil(width), { maxTier }));
  }

  /**
   * Bounds the clip to its loop window with a media fragment, so the preview only
   * decodes the opening it ever shows. Sequence mode plays each card to its end.
   * @param {string} src
   * @returns {string}
   */
  decorateSource(src) {
    if (this.engine.mode === SEQUENCE_MODE) return src;
    return `${src}#t=0,${PREVIEW_FRAGMENT_SECONDS}`;
  }

  /** Starts fetching and plays the preview. */
  start() {
    this.previewing = true;
    this.play();
  }

  /** Pauses the preview so off-screen cards stop playing. */
  stop() {
    this.previewing = false;
    this.pause();
  }

  /** Loops the preview back to the start after ~loopSeconds so only the opening is needed. */
  bindLoop() {
    const { video } = this;
    if (this.boundLoop || !video) return;
    this.boundLoop = true;

    const restart = () => {
      video.currentTime = 0;
      tryPlay(video);
    };
    video.addEventListener('ended', restart, { signal: this.signal });
    video.addEventListener(
      'timeupdate',
      () => {
        if (video.currentTime >= this.loopSeconds) restart();
      },
      { signal: this.signal }
    );
  }

  /**
   * Sequence mode: when this clip finishes, hand off to the next card.
   * @param {() => void} onEnded
   */
  bindSequenceEnd(onEnded) {
    if (this.boundSequence || !this.video) return;
    this.boundSequence = true;
    this.video.addEventListener('ended', onEnded, { signal: this.signal });
  }

  /**
   * Runs a handler the next time the clip starts playing, scoped to the clip so a
   * rebuild cannot leave it on the long-lived <video>.
   * @param {() => void} handler
   * @returns {() => void} Detaches the handler early.
   */
  onPlaying(handler) {
    const { video } = this;
    if (!video) return () => {};

    video.addEventListener('playing', handler, { once: true, signal: this.signal });
    return () => video.removeEventListener('playing', handler);
  }

  destroy() {
    super.destroy();
    this.media.classList.remove('is-preview-playing');
  }
}

/**
 * Drives the feed's preview playback: the IntersectionObserver, the staggered
 * autoplay queue and the sequence state machine. Owns one CardPreview per card,
 * so per-video state lives in the clip rather than on the DOM.
 */
class PreviewController {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {Map<Element, CardPreview>} */
  #clips = new Map();
  /** @type {Element[]} */
  #cards = [];
  /** @type {Set<Element>} */
  #sequenceVisible = new Set();
  /** @type {Element | null} */
  #sequenceActive = null;
  #sequencePlaying = false;
  /** @type {Element[]} */
  #autoplayQueue = [];
  #autoplayStarting = false;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #pumpTimer = null;
  /** Halts feed previews while the popup (or any overlay) covers the gallery. */
  #suspended = false;
  /** Sticky sound preference for the sequence; previews start muted so autoplay is allowed. */
  #muted = true;

  /** @param {Element} host - The gallery custom element. */
  constructor(host) {
    this.host = host;
  }

  get mode() {
    return this.host.getAttribute('data-video-preview');
  }

  get muted() {
    return this.#muted;
  }

  /** Flips the sequence sound and applies it to the card playing right now. */
  toggleMuted() {
    this.setMuted(!this.#muted);
  }

  /** @param {boolean} muted */
  setMuted(muted) {
    this.#muted = muted;
    this.#applyMuted();
  }

  /** Keeps every clip and every sound button on the current preference. */
  #applyMuted() {
    this.#clips.forEach((clip) => {
      if (clip.video) clip.video.muted = this.#muted;
    });
    this.host
      .querySelectorAll('.video-reviews-card__sound')
      .forEach((button) => button.classList.toggle('is-muted', this.#muted));
  }

  /**
   * (Re)builds the observer for the current mode. Uses threshold 0 + rootMargin
   * rather than an area ratio: portrait 9/16 cards are tall, so an area threshold
   * would only fire near the viewport center. No-op for cover_image or reduced
   * motion.
   */
  setup() {
    this.#observer?.disconnect();
    this.#destroyClips();
    this.#sequenceVisible = new Set();
    this.#sequenceActive = null;
    this.#sequencePlaying = false;
    this.#autoplayQueue = [];
    this.#autoplayStarting = false;
    this.#suspended = false;
    this.#cards = [];

    const mode = this.mode;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !mode || mode === 'cover_image') return;

    this.#observer = new IntersectionObserver((entries) => this.#handleIntersect(entries, mode), {
      rootMargin: '50px 0px',
      threshold: 0,
    });
    this.#cards = [...this.host.querySelectorAll(CARD_SELECTOR)];
    this.#cards.forEach((media) => this.#observer?.observe(media));
  }

  destroy() {
    this.#observer?.disconnect();
    clearTimeout(this.#pumpTimer);
    this.#autoplayQueue = [];
    this.#autoplayStarting = false;
    this.#destroyClips();
  }

  /**
   * Pauses every feed preview while an overlay (the popup, or a quick-add over it)
   * covers the gallery. Keeps the sequence/visibility state so resume() can pick up
   * the same card.
   */
  suspend() {
    if (this.#suspended) return;
    this.#suspended = true;
    clearTimeout(this.#pumpTimer);
    this.#autoplayQueue = [];
    this.#autoplayStarting = false;
    this.#sequencePlaying = false;
    this.#clips.forEach((clip) => clip.stop());
  }

  /**
   * Restarts feed previews after the popup closes: the sequence continues from the
   * card that was active before suspending, and autoplay-preview replays whichever
   * cards are still in view.
   */
  resume() {
    if (!this.#suspended) return;
    this.#suspended = false;
    if (!this.#observer) return;

    if (this.mode === SEQUENCE_MODE) {
      if (this.#sequenceActive && this.#sequenceVisible.has(this.#sequenceActive)) {
        this.#playSequenceCard(this.#sequenceActive);
      } else {
        this.#advanceSequence();
      }
      return;
    }

    this.host.querySelectorAll(`${CARD_SELECTOR}.is-previewing`).forEach((media) => {
      if (!this.#autoplayQueue.includes(media)) this.#autoplayQueue.push(media);
    });
    this.#pumpAutoplay();
  }

  #destroyClips() {
    this.#clips.forEach((clip) => clip.destroy());
    this.#clips.clear();
  }

  /**
   * @param {Element} media
   * @returns {CardPreview}
   */
  #clipFor(media) {
    let clip = this.#clips.get(media);
    if (!clip) {
      clip = new CardPreview(media, this, this.#loopSecondsFor(media));
      this.#clips.set(media, clip);
    }
    return clip;
  }

  /**
   * Staggers loop length by DOM position so adjacent previews never share a
   * restart cadence. Sequence mode plays each clip to the end, so length is moot.
   * @param {Element} media
   * @returns {number}
   */
  #loopSecondsFor(media) {
    if (this.mode === SEQUENCE_MODE) return PREVIEW_SECONDS;
    const index = this.#cards.indexOf(media);
    const step = index < 0 ? 0 : index % PREVIEW_LOOP_VARIANTS;
    return PREVIEW_SECONDS + step * PREVIEW_LOOP_STEP;
  }

  /**
   * Sequence mode: hovering a card jumps the sequence to it and plays it.
   * @param {PointerEvent} event
   */
  handlePointerOver(event) {
    if (this.mode !== SEQUENCE_MODE) return;
    const media = event.target?.closest?.(CARD_SELECTOR);
    if (!media || !this.host.contains(media) || media === this.#sequenceActive) return;
    this.#playSequenceBlock(media);
  }

  /**
   * Theme editor: selecting a block plays its card immediately (sequence only).
   * @param {string | undefined} blockId
   */
  playBlock(blockId) {
    if (this.mode !== SEQUENCE_MODE || !blockId) return;
    const media = this.host.querySelector(`.video-reviews-card[data-slide-block-id="${blockId}"]`);
    if (media) this.#playSequenceBlock(media);
  }

  /**
   * Autoplay-preview observer: queue cards entering view (staggered start so they
   * don't all fetch at once) and stop those leaving. Delegates sequence mode.
   * @param {IntersectionObserverEntry[]} entries
   * @param {string} mode
   */
  #handleIntersect(entries, mode) {
    if (mode === SEQUENCE_MODE) {
      this.#handleSequenceIntersect(entries);
      return;
    }

    for (const entry of entries) {
      const media = entry.target;

      if (entry.isIntersecting) {
        const clip = this.#clipFor(media);
        if (!clip.ensureVideo(entry.boundingClientRect.width)) continue;
        clip.bindLoop();
        media.classList.add('is-previewing');
        if (!this.#autoplayQueue.includes(media) && !clip.previewing) this.#autoplayQueue.push(media);
      } else {
        this.#autoplayQueue = this.#autoplayQueue.filter((card) => card !== media);
        this.#clips.get(media)?.stop();
        media.classList.remove('is-previewing');
      }
    }

    this.#pumpAutoplay();
  }

  /**
   * Plays queued previews one at a time: the next card starts only once the
   * current one is actually playing, so they load sequentially. A timeout guards
   * against 'playing' never firing (e.g. autoplay blocked).
   */
  #pumpAutoplay() {
    if (this.#suspended || this.#autoplayStarting) return;

    const media = this.#autoplayQueue.shift();
    if (!media) return;

    const clip = this.#clipFor(media);
    if (!clip.ensureVideo()) {
      this.#pumpAutoplay();
      return;
    }

    this.#autoplayStarting = true;
    let advanced = false;
    let detachAdvance = () => {};
    const advance = () => {
      if (advanced) return;
      advanced = true;
      clearTimeout(this.#pumpTimer);
      detachAdvance();
      this.#autoplayStarting = false;
      this.#pumpAutoplay();
    };
    detachAdvance = clip.onPlaying(advance);
    this.#pumpTimer = setTimeout(advance, 3000);

    clip.start();
  }

  /**
   * Updates the visible set for every entry first, then advances once, so the
   * first card to play is the DOM-first visible one regardless of the order the
   * observer reports entries in.
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleSequenceIntersect(entries) {
    let activeLeft = false;

    for (const entry of entries) {
      const media = entry.target;

      if (entry.isIntersecting) {
        this.#sequenceVisible.add(media);
      } else {
        this.#sequenceVisible.delete(media);
        media.classList.remove('is-sequence-active');
        this.#clips.get(media)?.stop();
        if (media === this.#sequenceActive) {
          this.#sequenceActive = null;
          activeLeft = true;
        }
      }
    }

    if (activeLeft) this.#sequencePlaying = false;
    if (!this.#sequencePlaying && !this.#suspended) this.#advanceSequence();
  }

  /**
   * Forces a specific card to play now (block select / hover).
   * @param {Element} media
   */
  #playSequenceBlock(media) {
    this.#sequenceVisible.add(media);
    this.#playSequenceCard(media);
  }

  /**
   * Visible cards in DOM order — the sequence cycles through these.
   * @returns {Element[]}
   */
  #visibleSequenceCards() {
    return this.#cards.filter((card) => this.#sequenceVisible.has(card));
  }

  /**
   * Plays the next visible card after the active one, in DOM order, wrapping back
   * to the first. Starts from the first visible when none is active.
   */
  #advanceSequence() {
    const cards = this.#visibleSequenceCards();
    if (!cards.length) {
      this.#sequencePlaying = false;
      this.#sequenceActive = null;
      return;
    }

    const current = this.#sequenceActive ? cards.indexOf(this.#sequenceActive) : -1;
    this.#playSequenceCard(cards[(current + 1) % cards.length]);
  }

  /**
   * Makes media the active sequence card: stops the others, plays this one from
   * the start, then (once it is playing) preloads the next for a smooth hand-off.
   * @param {Element} media
   */
  #playSequenceCard(media) {
    this.#sequenceVisible.forEach((card) => {
      if (card === media) return;
      card.classList.remove('is-sequence-active');
      this.#clips.get(card)?.stop();
    });

    this.#sequenceActive = media;
    this.#sequencePlaying = true;
    media.classList.add('is-sequence-active');

    const clip = this.#clipFor(media);
    if (!clip.ensureVideo()) {
      this.#sequencePlaying = false;
      return;
    }

    clip.bindSequenceEnd(() => this.#advanceSequence());
    if (clip.video.ended) clip.video.currentTime = 0;
    clip.video.muted = this.#muted;
    clip.start();
    clip.onPlaying(() => this.#preloadNext(media));
  }

  /**
   * Warms up the next visible card's video while the current one plays, so the
   * transition is smooth on the first pass instead of waiting on a fresh load.
   * @param {Element} currentMedia
   */
  #preloadNext(currentMedia) {
    const cards = this.#visibleSequenceCards();
    if (cards.length < 2) return;

    const index = cards.indexOf(currentMedia);
    if (index === -1) return;

    const next = cards[(index + 1) % cards.length];
    if (next === currentMedia) return;

    const clip = this.#clipFor(next);
    if (clip.ensureVideo()) clip.buffer();
  }
}

/**
 * Feed gallery: the row of video thumbnails. Delegates preview playback to a
 * PreviewController and handles opening the shoppable popup. The popup itself is a
 * separate <video-reviews-popup> component so its controls keep working when the
 * dialog is appended to <body> (the popup element travels with the dialog, the
 * gallery does not).
 */
export class VideoReviewsGallery extends Component {
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
  #startX = 0;
  #startY = 0;
  /** @type {Promise<void> | null} */
  #popupListPromise = null;
  #preview = new PreviewController(this);
  /** @type {string | null} Dialog id of the popup this gallery opened, for resume-on-close. */
  #activeDialogId = null;

  static DRAG_THRESHOLD = 8;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.abortController;

    if (!Shopify.designMode) {
      onIdleAfterPageLoad(() => {
        if (this.videoReviewsPopupList?.children.length) return;
        this.#intersectionObserver = observerElement({
          element: this,
          callback: () => this.loadPopupList(),
          threshold: '300',
        });
      });
    }

    this.addEventListener('pointerdown', this.#handlePointerDown, { passive: true, signal });
    this.addEventListener('pointerover', this.#handlePointerOver, { passive: true, signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });

    document.addEventListener(DialogOpenEvent.eventName, this.#handleFeedDialogOpen, { signal });
    document.addEventListener(DialogCloseEvent.eventName, this.#handleFeedDialogClose, { signal });

    if (Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    }

    if (this.dataset.productId) {
      this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#syncProductCard, { signal });

      onIdleAfterPageLoad(() => {
        const { variantId } = this.dataset;
        if (variantId) this.#eachPopupCard((card) => this.#setQuickAddVariant(card, variantId));
      });
    }

    this.#preview.setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#preview.destroy();
  }

  updatedCallback() {
    if (!Shopify.designMode && !this.videoReviewsPopupList?.children.length) {
      this.#popupListPromise = null;
      this.removeAttribute('data-loaded');
      this.loadPopupList();
    }
    this.#preview.setup();
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get videoReviewsPopupList() {
    return this.refs.videoReviewsPopupList;
  }

  /**
   * Lazy-loads the popup list once (section/stories only — the inline block popup
   * is already in the DOM). Scoped to this gallery's popup list by dialog id so
   * sibling blocks don't collide. Callers share the in-flight promise.
   * @returns {Promise<void>}
   */
  loadPopupList() {
    if (Shopify.designMode) return Promise.resolve();
    if (this.videoReviewsPopupList?.children.length) return Promise.resolve();

    const url = new URL('/', location.origin);
    url.searchParams.set('view', 'video-reviews-loader');

    const popupId = this.getAttribute('data-popup-id');
    const sourceSelector = popupId
      ? `[ref="videoReviewsPopupList"][data-popup-id="${popupId}"]`
      : '[ref="videoReviewsPopupList"]';

    return (this.#popupListPromise ??= sectionRenderer.fetchAndMorphContent({
      sectionId: this.sectionId,
      url,
      targetElement: this.videoReviewsPopupList,
      sourceSelector,
      cache: false,
      componentElement: this,
      checkLoadingStates: true,
    }));
  }

  /**
   * On variant change: update only the popup card's price and image (like the
   * price block), and point the quick-add popup at the active variant so it
   * opens pre-selected. The add-to-cart button itself is left untouched. Needed
   * because the popup is appended to <body>, outside the section, so the
   * section's own morph never reaches it.
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #syncProductCard = (event) => {
    const dialogId = this.getAttribute('data-popup-id');
    const variantId = event.detail.resource?.id;
    const freshCard = this.#findCard(event.detail.data.html?.querySelector(`[id="${dialogId}"]`));

    this.#eachPopupCard((card) => {
      if (freshCard) {
        for (const selector of ['.product-card-compact__image', '.product-card__price']) {
          const fresh = freshCard.querySelector(selector);
          const current = card.querySelector(selector);
          if (fresh && current) morph(current, fresh.cloneNode(true));
        }
      }
      this.#setQuickAddVariant(card, variantId);
    });
  };

  /**
   * The popup product card under a dialog root, whether the dialog content is
   * wrapped in a <template> (storefront) or rendered inline (theme editor).
   * @param {ParentNode | null | undefined} dialogRoot
   * @returns {Element | null}
   */
  #findCard(dialogRoot) {
    return (
      dialogRoot?.querySelector('template[ref="dialogTemplate"]')?.content.querySelector(
        '.video-review-popup__product'
      ) ??
      dialogRoot?.querySelector('.video-review-popup__product') ??
      null
    );
  }

  /**
   * Run a callback for this block's popup product card. The dialog's inner
   * element moves to <body> when opened (tagged with a deterministic owner id),
   * so the card lives under either the in-place component or the moved dialog.
   * @param {(card: Element) => void} callback
   */
  #eachPopupCard(callback) {
    const dialogId = this.getAttribute('data-popup-id');
    if (!dialogId) return;

    const roots = [
      document.getElementById(dialogId),
      document.querySelector(`[data-dialog-owner="dialog-owner-${dialogId}"]`),
    ];
    for (const root of roots) {
      const card = this.#findCard(root);
      if (card) callback(card);
    }
  }

  /**
   * Rewrite the quick-add product link so the picker popup opens on the active
   * variant. Leaves the button markup as-is.
   * @param {Element} card
   * @param {string | number | null | undefined} variantId
   */
  #setQuickAddVariant(card, variantId) {
    if (!variantId) return;
    const quickAdd = card.querySelector('.quick-add-compact');
    if (!quickAdd) return;
    const base = (quickAdd.getAttribute('data-product-link') || '').split('?')[0];
    if (base) quickAdd.setAttribute('data-product-link', `${base}?variant=${variantId}`);
  }

  #handlePointerDown = (event) => {
    this.#startX = event.clientX;
    this.#startY = event.clientY;
    if (event.target?.closest?.('.video-reviews-card[data-dialog-id]')) this.loadPopupList();
  };

  #handlePointerOver = (event) => this.#preview.handlePointerOver(event);

  #onBlockSelect = (event) => this.#preview.playBlock(/** @type {CustomEvent} */ (event).detail?.blockId);

  #handleClick = (event) => {
    const card = event.target?.closest?.('.video-reviews-card[data-dialog-id]');
    if (!card || !this.contains(card)) return;

    if (event.target.closest('.video-reviews-card__author-link')) return;

    if (event.target.closest('.video-reviews-card__sound')) {
      event.preventDefault();
      event.stopPropagation();
      this.#preview.toggleMuted();
      return;
    }

    const threshold = VideoReviewsGallery.DRAG_THRESHOLD;
    const dragged =
      Math.abs(event.clientX - this.#startX) > threshold || Math.abs(event.clientY - this.#startY) > threshold;
    if (dragged) return;

    event.preventDefault();
    event.stopPropagation();
    this.#openDialog(card);
  };

  #handleKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const card = event.target?.closest?.('.video-reviews-card[data-dialog-id]');
    if (!card || !this.contains(card)) return;

    if (event.target.closest('.video-reviews-card__sound')) return;

    event.preventDefault();
    this.#openDialog(card);
  };

  /**
   * Opens the dialog for the clicked card, then navigates the popup to its slide.
   * Opening clones the popup out of its <template> (and may need a lazy fetch for
   * section/stories), so the popup is looked up afterwards by dialog id.
   * @param {Element} card
   */
  async #openDialog(card) {
    const dialogId = card.getAttribute('data-dialog-id');
    if (!dialogId) return;

    let dialog = /** @type {any} */ (document.getElementById(dialogId));
    if (!dialog?.openDialog) {
      this.setAttribute('aria-busy', 'true');
      await this.loadPopupList();
      this.removeAttribute('aria-busy');

      dialog = document.getElementById(dialogId);
      if (!dialog?.openDialog) {
        this.#popupListPromise = null;
        return;
      }
    }

    dialog.openDialog();
    this.#activeDialogId = dialogId;
    this.#preview.suspend();
    resetSrcsetOnMobile(dialog);
    const popup = this.#findPopup(dialogId);
    popup?.setMuted?.(this.#preview.muted);
    popup?.goTo?.(card.getAttribute('data-slide-block-id'));
  }

  /**
   * Any overlay covers every gallery on the page — a video review popup, but equally the
   * cart drawer, search or a newsletter popup — so all of them stop previewing. A gallery
   * that sits inside an overlay of its own is on top and keeps going.
   * @param {Event} event
   */
  #handleFeedDialogOpen = (event) => {
    if (!(event.target instanceof Element) || this.closest('.dialog[open]')) return;
    this.#preview.suspend();
  };

  /**
   * Resume feed previews once no overlay is left covering the gallery, so a quick-add
   * closing over the popup leaves previews paused until the popup itself is dismissed.
   * Only the gallery that opened the popup resets the sound preference.
   * @param {Event} event
   */
  #handleFeedDialogClose = (event) => {
    if (!(event.target instanceof Element)) return;

    if (this.#activeDialogId && event.target.id === this.#activeDialogId) {
      this.#activeDialogId = null;
      this.#preview.setMuted(true);
    }

    if (document.querySelector('.dialog[open]')) return;

    this.#preview.resume();
  };

  /**
   * @param {string} dialogId
   * @returns {(Element & { goTo?: (blockId: string | null) => void }) | null}
   */
  #findPopup(dialogId) {
    return document.querySelector(`video-reviews-popup[data-dialog-id="${dialogId}"]`);
  }
}

/**
 * Shoppable popup: the dialog carousel of review videos with play/sound/nav and
 * (on mobile) the tagged-products drawer. Wraps its own <carousel-component>, so
 * it stays self-contained and its on:click controls keep resolving even when the
 * dialog is appended to <body> (the whole element moves together).
 */
export class VideoReviewsPopup extends Component {
  /** @type {Element | null} */
  #activeMedia = null;
  /** @type {Element | null | undefined} Slide the ±1 window was last primed around; undefined until it is. */
  #primedFor = undefined;
  /** @type {Map<Element, PopupClip>} */
  #clips = new Map();
  /** @type {number | null} */
  #progressRaf = null;
  /** @type {boolean} Sticky sound preference across slides; the gallery sets it from the feed on open. */
  #muted = true;
  /** @type {AbortController | null} */
  #drawerDragController = null;
  /** @type {(Function & { cancel?: () => void }) | null} */
  #handleResize = null;

  /** Carousel options per layout: horizontal cross-fades, vertical is the reels. */
  static POPUP_OPTIONS = {
    horizontal: { slidesPerView: 1, speed: 400, loop: false, grabCursor: false, effect: 'fade', fadeEffect: { crossFade: true } },
    vertical: { slidesPerView: 1, speed: 400, loop: false, grabCursor: false, direction: 'vertical' },
  };

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.abortController;

    this.addEventListener('pointerdown', this.#handlePointerDown, { passive: true, signal });
    document.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
    document.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

    this.#handleResize = debounce(() => this.#applyEffect(), 50);
    window.addEventListener('resize', this.#handleResize, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#deactivateMedia();
    this.#clips.forEach((clip) => clip.destroy());
    this.#clips.clear();
    this.#drawerDragController?.abort();
    this.#handleResize?.cancel?.();
  }

  /** The dialog-component that owns this popup; it stays put when the dialog is moved to body. */
  get #dialog() {
    return /** @type {any} */ (document.getElementById(this.getAttribute('data-dialog-id') ?? ''));
  }

  get #carousel() {
    return this.querySelector('carousel-component.video-reviews-swiper');
  }

  /**
   * Vertical-reels popups (product-page block) opt in via data-popup-layout;
   * otherwise the layout follows the viewport.
   * @returns {'vertical' | 'horizontal'}
   */
  get #popupMode() {
    if (this.getAttribute('data-popup-layout') === 'vertical') return 'vertical';
    return isMobileBreakpoint() ? 'vertical' : 'horizontal';
  }

  /**
   * Navigates to the slide for the opened card. The gallery opens the dialog
   * (which clones this popup out of its <template>), then calls this.
   * @param {string | null} blockId
   */
  goTo(blockId) {
    this.#goToSlide(blockId);
  }

  /**
   * Opens on whatever the feed was doing: the sequence card is the one the
   * shopper was just watching, so its sound carries into the popup.
   * @param {boolean} muted
   */
  setMuted(muted) {
    this.#muted = muted;
  }

  get muted() {
    return this.#muted;
  }

  /**
   * @param {Element | null | undefined} media
   * @returns {PopupClip | null}
   */
  #clipFor(media) {
    if (!media) return null;
    let clip = this.#clips.get(media);
    if (!clip) {
      clip = new PopupClip(media);
      this.#clips.set(media, clip);
    }
    return clip;
  }

  /** @returns {Element[]} */
  #slides() {
    return Array.from(this.querySelectorAll('.video-review-slide'));
  }

  /** Wires the active slide's neighbours at `preload=metadata`; from the native pointerdown gesture this also unlocks them, once each, for iOS sounded playback on the swipe. */
  primeVideos() {
    if (this.#primedFor === this.#activeMedia) return;
    this.#primedFor = this.#activeMedia;

    const slides = this.#slides();
    let center = slides.indexOf(this.#activeMedia?.closest('.video-review-slide'));
    if (center < 0) center = 0;
    for (let offset = -POPUP_PRIME_RADIUS; offset <= POPUP_PRIME_RADIUS; offset++) {
      const media = slides[center + offset]?.querySelector?.(POPUP_MEDIA);
      if (!media || media === this.#activeMedia) continue;
      this.#clipFor(media)?.prime();
    }
  }

  closePopup(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.#dialog?.closeDialog?.();
  }

  #handleDialogClose = (event) => {
    if (event.target !== this.#dialog) return;
    this.#deactivateMedia();
    this.#clips.forEach((clip) => clip.detach());
  };

  /**
   * A quick add / quick view (or any other dialog) opening over the popup pauses
   * the playing video so it does not keep running behind the new popup. Ignores
   * this popup's own dialog opening.
   */
  #handleDialogOpen = (event) => {
    if (!this.#activeMedia || event.target === this.#dialog) return;
    this.#clips.get(this.#activeMedia)?.pause();
    this.#activeMedia.classList.add('is-paused');
  };

  /**
   * Primes the neighbouring clips — native pointerdown is a real user gesture,
   * unlike Swiper's synthetic touchEnd, so it unlocks them for sounded playback.
   * Dragging the mobile products drawer down by its header also closes it.
   */
  #handlePointerDown = (event) => {
    this.primeVideos();
    if (event.target?.closest?.('.video-review-popup__drawer-header')) {
      this.#startDrawerDrag(event);
    }
  };

  /**
   * @param {string | null} blockId
   */
  #goToSlide(blockId) {
    this.#applyEffect(false);
    const swiper = this.#carousel?.swiper;
    if (!swiper) return;

    swiper.update();

    const slides = this.#slides();
    const index = slides.findIndex((slide) => slide.getAttribute('data-block-id') === blockId);
    const targetIndex = index >= 0 ? index : 0;

    swiper.slideTo(targetIndex, 0);
    this.#bindSwiperPlayback(swiper);
    this.#activateSlide(slides[targetIndex]);
  }

  /**
   * Rebuilds the carousel for the current viewport, skipping the rebuild when it
   * was already built for this mode. Swiper locks `effect` at init, so changing
   * it means rebuilding the carousel.
   */
  #applyEffect = (activate = true) => {
    const carousel = this.#carousel;
    if (!carousel) return;

    const mode = this.#popupMode;
    if (carousel.swiper && carousel.dataset.popupMode === mode) return;

    const activeIndex = carousel.swiper?.activeIndex ?? 0;
    carousel.setAttribute('data-options', JSON.stringify(VideoReviewsPopup.POPUP_OPTIONS[mode]));
    carousel.dataset.popupMode = mode;
    carousel.reinitFromOptions?.();

    const rebuilt = carousel.swiper;
    if (!rebuilt) return;
    rebuilt.slideTo(activeIndex, 0);
    this.#bindSwiperPlayback(rebuilt);
    if (activate && carousel.closest('.dialog')?.hasAttribute('open')) {
      this.#activateSlide(rebuilt.slides?.[activeIndex]);
    }
  };

  /**
   * @param {object} swiper
   */
  #bindSwiperPlayback(swiper) {
    if (!swiper || swiper.videoReviewsBound) return;
    swiper.videoReviewsBound = true;
    this.#updateNavState(swiper);
    swiper.on('slideChange', () => {
      if (!this.#carousel?.closest('.dialog')?.hasAttribute('open')) return;
      this.#activateSlide(swiper.slides?.[swiper.activeIndex]);
      this.#updateNavState(swiper);
    });
  }

  /**
   * Disables prev on the first slide and next on the last; native [disabled]
   * styling handles the dimmed look.
   * @param {object} swiper
   */
  #updateNavState(swiper) {
    this.querySelectorAll('.video-review-popup__previous').forEach((btn) => (btn.disabled = swiper.isBeginning));
    this.querySelectorAll('.video-review-popup__next').forEach((btn) => (btn.disabled = swiper.isEnd));
  }

  /**
   * @param {Element | undefined} slide
   */
  #activateSlide(slide) {
    const media = slide?.querySelector(POPUP_MEDIA);
    if (!media || media === this.#activeMedia) return;

    this.#deactivateMedia();
    this.#activeMedia = media;

    const clip = this.#clipFor(media);
    clip?.attach();
    clip?.mute(this.#muted);
    clip?.play();

    this.querySelectorAll('.video-review-popup__sound').forEach((btn) => btn.classList.toggle('is-muted', this.#muted));
    media.classList.remove('is-paused');
    slide.querySelector('.video-review-popup')?.classList.remove('is-drawer-open');

    requestAnimationFrame(() => this.#bindProgress(media));
  }

  /**
   * @param {Element} media
   */
  #bindProgress(media) {
    if (media !== this.#activeMedia) return;
    const video = this.#clips.get(media)?.video;
    const progress = media.querySelector('.video-review-popup__progress');
    if (!video || !progress) return;

    if (this.#progressRaf) cancelAnimationFrame(this.#progressRaf);
    const tick = () => {
      if (!video.paused && video.duration) {
        progress.style.setProperty('--progress', (video.currentTime / video.duration).toFixed(4));
      }
      this.#progressRaf = requestAnimationFrame(tick);
    };
    this.#progressRaf = requestAnimationFrame(tick);
  }

  #deactivateMedia() {
    if (this.#progressRaf) cancelAnimationFrame(this.#progressRaf);
    this.#progressRaf = null;
    this.#clips.forEach((clip) => {
      clip.pause();
      clip.mute(true);
    });
    this.#activeMedia = null;
    this.#primedFor = undefined;
  }

  togglePlay(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const media = event.target.closest('.video-review-slide')?.querySelector(POPUP_MEDIA);
    const clip = this.#clipFor(media);
    if (!clip) return;

    const willPlay = !clip.isPlaying;
    if (willPlay) clip.play();
    else clip.pause();
    media.classList.toggle('is-paused', !willPlay);
  }

  toggleSound(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const button = event.target.closest('.video-review-popup__sound');
    const media = event.target.closest('.video-review-slide')?.querySelector(POPUP_MEDIA) ?? this.#activeMedia;
    const clip = this.#clipFor(media);
    if (!button || !clip) return;

    const willMute = !button.classList.contains('is-muted');
    this.#muted = willMute;
    clip.mute(willMute);
    button.classList.toggle('is-muted', willMute);
  }

  toggleDrawer(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const popup = event.target.closest('.video-review-popup') ?? this.querySelector('.swiper-slide-active .video-review-popup');
    const open = popup?.classList.toggle('is-drawer-open');
    this.#setSwiperInteractive(!open);
  }

  /**
   * Enables/disables the carousel's touch + wheel navigation.
   * @param {boolean} enabled
   */
  #setSwiperInteractive(enabled) {
    const swiper = this.#carousel?.swiper;
    if (!swiper) return;
    swiper.allowTouchMove = enabled;
    enabled ? swiper.mousewheel?.enable?.() : swiper.mousewheel?.disable?.();
  }

  /**
   * Drag the mobile products drawer down by its header to close it — mirrors the
   * dialog component's drag-to-dismiss (RAF updates, 20%-viewport threshold, only
   * downward); release past the threshold closes, otherwise it snaps back.
   * @param {PointerEvent} event
   */
  #startDrawerDrag(event) {
    const details = event.target.closest('.video-review-popup__details');
    const popup = details?.closest('.video-review-popup');
    if (!details || !popup) return;

    const startY = event.clientY;
    const threshold = Math.max(80, Math.floor(0.2 * window.innerHeight));
    let delta = 0;
    let rafId = null;

    this.#drawerDragController?.abort();
    this.#drawerDragController = new AbortController();
    const { signal } = this.#drawerDragController;
    details.style.transition = 'none';

    document.addEventListener(
      'pointermove',
      (moveEvent) => {
        delta = Math.max(0, moveEvent.clientY - startY);
        if (!rafId) {
          rafId = requestAnimationFrame(() => {
            details.style.transform = `translateY(${delta}px)`;
            rafId = null;
          });
        }
      },
      { signal }
    );

    document.addEventListener(
      'pointerup',
      () => {
        if (rafId) cancelAnimationFrame(rafId);
        details.style.transition = '';
        if (delta > threshold) {
          popup.classList.remove('is-drawer-open');
          this.#setSwiperInteractive(true);
        }
        details.style.transform = '';
        this.#drawerDragController?.abort();
        this.#drawerDragController = null;
      },
      { signal }
    );
  }

  prevPopup(event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.#slideTo('prev');
  }

  nextPopup(event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.#slideTo('next');
  }

  /**
   * @param {'prev' | 'next'} direction
   */
  #slideTo(direction) {
    const swiper = this.#carousel?.swiper;
    if (!swiper) return;
    direction === 'prev' ? swiper.slidePrev() : swiper.slideNext();
  }
}

if (!customElements.get('video-reviews-gallery')) {
  customElements.define('video-reviews-gallery', VideoReviewsGallery);
}

if (!customElements.get('video-reviews-popup')) {
  customElements.define('video-reviews-popup', VideoReviewsPopup);
}
